import { useEffect, useState } from "react";
import api from "../../lib/api";
import { PlayCircle, X, YoutubeLogo } from "@phosphor-icons/react";

/**
 * Homepage video tour — admin-curated YouTube clips that auto-play in a modal.
 * Each click fires `/api/public/landing-videos/{id}/play` for watch-rate
 * analytics in Admin → Landing videos. Section auto-hides if no videos are
 * published, so the landing stays clean during empty states.
 */
export default function LandingVideoTour() {
  const [videos, setVideos] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/public/landing-videos")
      .then(({ data }) => { if (alive) setVideos(Array.isArray(data) ? data : []); })
      .catch(() => { /* feature optional — landing stays usable */ });
    return () => { alive = false; };
  }, []);

  if (!videos.length) return null;

  const open = (v) => {
    setActive(v);
    api.post(`/public/landing-videos/${v.id}/play`).catch(() => null);
  };

  return (
    <section id="video-tour" className="border-t hairline" data-testid="landing-video-tour">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-6">
          <div className="max-w-2xl">
            <div className="eyebrow mb-3 flex items-center gap-2">
              <YoutubeLogo size={14} weight="fill" className="text-[hsl(var(--primary))]" />
              <span>Video tour</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              90 seconds to see how Estima decides.
            </h2>
            <p className="text-muted-foreground mt-4 text-sm">
              Short walkthroughs of every flow — before you sign up, before you commit.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="see-it-videos">
          {videos.map((v) => (
            <button
              key={v.id}
              onClick={() => open(v)}
              className="card-flat p-0 overflow-hidden text-left hover:border-[hsl(var(--secondary))] transition group"
              data-testid={`see-it-video-${v.id}`}
            >
              <div className="aspect-video bg-muted relative overflow-hidden">
                {v.thumbnail && (
                  <img
                    src={v.thumbnail}
                    alt={v.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition duration-500"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-90" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-14 w-14 rounded-full bg-[hsl(var(--primary))]/90 flex items-center justify-center group-hover:scale-110 transition">
                    <PlayCircle size={28} weight="fill" className="text-white" />
                  </div>
                </div>
                <span className="absolute bottom-3 left-3 text-[10px] uppercase tracking-wider text-[hsl(var(--secondary))] bg-black/60 px-2 py-1 inline-flex items-center gap-1">
                  <YoutubeLogo size={11} weight="fill" /> Video
                </span>
              </div>
              <div className="p-5">
                <div className="font-serif text-lg mb-1 group-hover:text-[hsl(var(--secondary))] transition">{v.title}</div>
                {v.description && <p className="text-xs text-muted-foreground line-clamp-2">{v.description}</p>}
              </div>
            </button>
          ))}
        </div>
      </div>

      {active && <VideoModal video={active} onClose={() => setActive(null)} />}
    </section>
  );
}

function VideoModal({ video, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 sm:p-6"
      onClick={onClose}
      data-testid="see-it-video-modal"
    >
      <div
        className="bg-card border hairline w-full max-w-5xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 sm:px-6 py-3 border-b hairline flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="eyebrow text-[hsl(var(--secondary))] text-[10px] flex items-center gap-1">
              <YoutubeLogo size={11} weight="fill" /> Video
            </div>
            <div className="font-serif text-base sm:text-lg truncate" data-testid="see-it-video-title">{video.title}</div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition"
            aria-label="Close"
            data-testid="see-it-video-close"
          >
            <X size={20} />
          </button>
        </header>
        <div className="aspect-video bg-black">
          {video.embed_url && (
            <iframe
              key={video.id}
              src={`${video.embed_url}&autoplay=1`}
              title={video.title}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="see-it-video-iframe"
            />
          )}
        </div>
        {video.description && (
          <div className="px-5 sm:px-7 py-4 border-t hairline">
            <p className="text-sm leading-relaxed text-muted-foreground" data-testid="see-it-video-description">{video.description}</p>
          </div>
        )}
      </div>
    </div>
  );
}
